import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import Markdown from 'shared/components/Markdown';
import { SPACINGS } from 'shared/styles/variables';
import styled, { ThemeContext } from 'styled-components';

import ImageFailed from 'images/pictographs/id-failed.svg';
import Button from 'inquiry/components/Button';
import { PictographOption } from 'lib/interfaces/StepsConfig';
import ThemeSerialized, { PictographPosition, TextAlign } from 'lib/interfaces/ThemeSerialized';
import CenteredPictograph from 'verify/components/CenteredPictograph';
import CustomizablePictograph from 'verify/components/CustomizablePictograph';
import PositionableButtonContainer from 'verify/components/PositionableButtonContainer';
import ThemedTitle from 'verify/components/ThemedTitle';
import FormSupportTicket from 'verify/forms/FormSupportTicket';
import { MIN_HEIGHTS } from 'verify/styles/variables';

const Header = styled('div')`
  min-height: ${MIN_HEIGHTS.header};
  margin-bottom: ${SPACINGS.base * 4}px;
`;

// [PER-7484]: Defaults to centered text to preserve Persona-opinionated alignment.
// Change supports full explicit start-alignment for DoorDash request.
const TextDefaultCentered = styled('div')<{ theme: ThemeSerialized }>`
  text-align: ${({ theme }) =>
    theme.textAlign === TextAlign.Default ? 'center' : theme.textAlign};
`;

const SupportContent = styled('div')``;

interface VerifyFailedViewProps {
  handleClose: () => void;
  handleSubmitTicket: (values: any) => void;
  hasSupport: boolean;
  hasCompleteButton: boolean;
  pictograph: PictographOption;
}

export default function VerifyFailedView({
  handleClose,
  handleSubmitTicket,
  hasSupport,
  hasCompleteButton,
  pictograph,
}: VerifyFailedViewProps) {
  const [t] = useTranslation();
  const { failedPictographPosition } = useContext(ThemeContext);

  const pictographNode = pictograph !== 'none' && (
    <CenteredPictograph aria-label='failed pictograph'>
      <CustomizablePictograph url='failedPictographUrl' height='failedPictographHeight'>
        <ImageFailed style={{ width: '120px', height: '120px' }} />
      </CustomizablePictograph>
    </CenteredPictograph>
  );

  if (hasSupport) {
    return (
      <SupportContent>
        <Header>
          <ThemedTitle>
            {/* We don't use the new ThemedParagraph for backwards compatibility; this is centered by default. */}
            <TextDefaultCentered>{t('failed.title-support')}</TextDefaultCentered>
          </ThemedTitle>
          <TextDefaultCentered>{t('failed.prompt-support')}</TextDefaultCentered>
        </Header>
        <FormSupportTicket onSubmit={handleSubmitTicket} />
      </SupportContent>
    );
  } else {
    return (
      <div>
        {failedPictographPosition === PictographPosition.Start && pictographNode}

        <Header>
          <ThemedTitle>
            {/* We don't use the new ThemedParagraph for backwards compatibility; this is centered by default. */}
            <TextDefaultCentered>{t('failed.title')}</TextDefaultCentered>
          </ThemedTitle>
          <TextDefaultCentered>
            <Markdown>{t('failed.prompt')}</Markdown>
          </TextDefaultCentered>
        </Header>

        {failedPictographPosition === PictographPosition.Center && pictographNode}

        {hasCompleteButton && (
          <PositionableButtonContainer>
            <Button onClick={handleClose} id='failed__button' data-test='failed__button'>
              {t('failed.btn-submit')}
            </Button>
          </PositionableButtonContainer>
        )}
      </div>
    );
  }
}
